import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  CircularProgress,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Mic,
  MicOff,
  Settings as SettingsIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Header from "./layouts/Header";
import { PointCloud } from "./point-cloud/PointCloud";
import { JobSuggestion } from "./job-suggestion/JobSuggestion";
import { useLiveAPIContext } from "../core/live-api/LiveAPIContext";
import { Job } from "../types";
import { AudioRecorder } from "../lib/audio-recorder";
import { useSession } from "../contexts/SessionContext";
import {
  DEFAULT_VOICE,
  getDefaultLanguage,
  getAvailableLanguages,
} from "../config";
import { t } from "../translations";
import { getGeminiToken, updateRecommendationAction } from "../services/api";
import { ProfilePreview } from "./profile-preview/ProfilePreview";
import { FeedbackForm } from "./feedback/FeedbackForm";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { EndConversationDialog } from "./EndConversationDialog";
import { TermsAndConditionsDialog } from "./TermsAndConditionsDialog";
import { useNetworkStatus } from "../lib/use-network-status";

const formatValue = (value: any) => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

type JobAction = "interested" | "not_interested";

const MainContent: React.FC<{
  isLoadingJobs: boolean;
  suggestedJobs: any[];
  isShowingJobs: boolean;
  setIsShowingJobs: (isShowing: boolean) => void;
  sessionId?: string | null;
  memoryKv: any;
  latestProfileEntry: any;
  userId: string | undefined;
  countryCode: string;
  restrictionAnalysis: Record<string, { count: number; percentage: number }>;
}> = ({
  isLoadingJobs,
  suggestedJobs,
  isShowingJobs,
  setIsShowingJobs,
  sessionId,
  memoryKv,
  latestProfileEntry,
  userId,
  countryCode,
  restrictionAnalysis,
}) => {
  const {
    client,
    connected,
    connect,
    disconnect,
    volume,
    isResumedSession,
    confirmEndConversation,
    isResuming,
  } = useLiveAPIContext();
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [isMuted, setIsMuted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const availableLanguages = getAvailableLanguages(countryCode);
  const defaultLanguage = getDefaultLanguage(countryCode);

  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage");
    // Only use saved language if it's valid for this country
    if (savedLanguage && availableLanguages.includes(savedLanguage)) {
      return savedLanguage;
    }
    return defaultLanguage;
  });

  // Reset language to country default when country code is set
  // Only use saved language if it was explicitly saved by the user
  useEffect(() => {
    if (countryCode) {
      const savedLanguage = localStorage.getItem("selectedLanguage");
      // Reset to default if no saved language or saved language not valid for this country
      if (!savedLanguage || !availableLanguages.includes(savedLanguage)) {
        setLanguage(defaultLanguage);
      }
    }
  }, [countryCode, availableLanguages, defaultLanguage]);

  const micButtonRef = useRef<HTMLButtonElement>(null);
  const [isProfileExpanded, setIsProfileExpanded] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState<"report" | "feedback">(
    "report",
  );
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(() => {
    return localStorage.getItem("termsAccepted") === "true";
  });
  const [showSettings, setShowSettings] = useState(false);
  const isOnline = useNetworkStatus();

  const { createSession, endSession, isSessionActive, checkSessionStatus } =
    useSession();
  const [feedbackFromSessionEnd, setFeedbackFromSessionEnd] = useState(false);
  const jobActionsRef = useRef<Record<string, JobAction>>({});

  useEffect(() => {
    if (connected) {
      setIsConnecting(false);
    }
  }, [connected]);

  useEffect(() => {
    localStorage.setItem("selectedLanguage", language);
  }, [language]);

  useEffect(() => {
    if (!showFeedback && feedbackFromSessionEnd) {
      (async () => {
        // The session ends only after the automatically triggered feedback form is closed.
        try {
          await endSession();
          console.log("Session ended after feedback closed.");
        } catch (err) {
          console.error("Failed to end session:", err);
        } finally {
          setFeedbackFromSessionEnd(false);
        }
      })();
    }
  }, [showFeedback, feedbackFromSessionEnd, endSession]);

  useEffect(() => {
    const handleConversationEnd = async () => {
      if (connected) {
        setShowEndDialog(true);
      }
    };

    client.on("conversationend", handleConversationEnd);

    return () => {
      client.off("conversationend", handleConversationEnd);
    };
  }, [client, connected]);

  useEffect(() => {
    if (sessionId && client) {
      client.setSessionId(sessionId);
    }
  }, [sessionId, client]);

  useEffect(() => {
    if (connected && !isResumedSession) {
      client.send({
        text: "The user is here. Please start your introduction now.",
      });
      console.log("DEBUG: Sent initial message to model.");
    }
  }, [connected, client, isResumedSession]);

  useEffect(() => {
    if (!connected) return;

    const onData = (base64: string) => {
      if (isMuted) return;
      if (isResuming) return;
      if (client) {
        client.sendRealtimeInput([
          {
            mimeType: "audio/pcm;rate=16000",
            data: base64,
          },
        ]);
      }
    };

    const onVolume = (volume: number) => {
      if (micButtonRef.current && !isMuted) {
        micButtonRef.current.style.transform = `scale(${1 + volume * 2})`;
      } else if (micButtonRef.current && isMuted) {
        micButtonRef.current.style.transform = "scale(1)";
      }
    };

    const startMic = async () => {
      try {
        if (!audioRecorder.recording) {
          console.log("Starting microphone…");
          await audioRecorder.start();
          console.log("Microphone started.");
        }
      } catch (err) {
        console.error("Mic start failed:", err);
      }
    };

    startMic();

    audioRecorder.on("data", onData);
    audioRecorder.on("volume", onVolume);

    return () => {
      audioRecorder.off("data", onData);
      audioRecorder.off("volume", onVolume);
    };
  }, [connected, client, isMuted,isResuming, audioRecorder]);

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleEndConfirm = async () => {
    setShowEndDialog(false);
    confirmEndConversation(true);
    await disconnect();
    setFeedbackFromSessionEnd(true);
    setFeedbackMode("feedback");
    setShowFeedback(true);
    console.log("Session ended");
  };

  const handleAcceptTerms = () => {
    localStorage.setItem("termsAccepted", "true");
    setHasAcceptedTerms(true);
  };

  const handleEndCancel = () => {
    confirmEndConversation(false);
    setShowEndDialog(false);
  };

  const micButtonStyle = {
    transition: "transform 0.1s ease-out",
    backgroundColor: isMuted ? "rgba(0, 0, 0, 0.12)" : "rgb(33, 117, 217)",
    color: "white",
    width: "60px",
    height: "60px",
    "&:hover": {
      backgroundColor: isMuted ? "rgba(0, 0, 0, 0.2)" : "rgb(45, 129, 229)",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
    },
  };

  const handleConnectionToggle = async () => {
    if (connected) {
      setShowEndDialog(true);
      return;
    }

    if (isConnecting) {
      return;
    }

    setIsConnecting(true);
    let newSessionId: string | undefined;
    let sessionNeedsCleanup = false;

    try {
      console.log("Starting connection process...");

      let currentSessionId = sessionId;
      if (!currentSessionId || !isSessionActive) {
        console.log("No active session, creating new one...");
        if (!userId) {
          throw new Error("User ID not available");
        }
        newSessionId = await createSession(userId);
        if (!newSessionId) {
          throw new Error("Failed to create a session.");
        }
        currentSessionId = newSessionId;
        sessionNeedsCleanup = true;
        console.log(`Session created with ID: ${currentSessionId}`);
      }

      const isLive = await checkSessionStatus(currentSessionId);
      if (!isLive) {
        throw new Error("Session is not active on the backend.");
      }
      console.log("Session is confirmed active.");

      client.setSessionId(currentSessionId);

      const token = await getGeminiToken();
      await connect(language, countryCode, DEFAULT_VOICE, token);

      console.log("Connection to Gemini Live successful.");
    } catch (error) {
      console.error("Connection process failed:", error);
      if (sessionNeedsCleanup && newSessionId) {
        await endSession(newSessionId);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleJobPreference = useCallback(
    async (job: Job, interested: boolean) => {
      const action = interested ? "interested" : "not_interested";
      const jobId = String(job.recommendation_id ?? "unknown");

      jobActionsRef.current[jobId] = action;

      // Save to database (single call)
      if (job.recommendation_id != null) {
        try {
          await updateRecommendationAction(job.recommendation_id, action);
          console.log(
            `Saved user action "${action}" for recommendation ${job.recommendation_id}`,
          );
        } catch (error) {
          console.error("Failed to save user action to database:", error);
        }
      } else {
        console.warn("Missing recommendation_id on job:", job);
      }

      // Send structured message to AI - no "make a note" to avoid agentic behavior
      const msg = `[USER_ACTION]
job_id: ${job.recommendation_id || "unknown"}
title: ${job.title}
action: ${action}
[/USER_ACTION]
Acknowledge briefly. Do NOT advance to another job until you receive a new [JOB_CONTEXT] block.`;

      client.send({ text: msg });
      console.log("DEBUG: Sending user action to model:", msg);
    },
    [client],
  );

  const handleJobCompletion = useCallback(() => {
    const actionsMap = jobActionsRef.current;
    const jobLines = suggestedJobs.map((job: any) => {
      const id = String(job.recommendation_id ?? "unknown");
      const title = job.title ?? "unknown";
      const action = actionsMap[id] ?? "unknown";
      return `- job_id: ${id} | title: "${title}" | action: ${action}`;
    });

    const actions = Object.values(actionsMap);
    const interestedCount = actions.filter((a) => a === "interested").length;
    const notInterestedCount = actions.filter(
      (a) => a === "not_interested",
    ).length;

    const criticalLine =
      interestedCount > 0
        ? `\nIMPORTANT: interested is bigger than 0. DO NOT ENTER <zero_interested_flow>! Do NOT start saying "I see that none of the jobs suggestions ...".\n`
        : "";

    const recapMsg = `[JOB_PREFERENCES]
total_jobs_shown = ${suggestedJobs.length}
preferences_recorded = ${actions.length}
interested = ${interestedCount}
not_interested = ${notInterestedCount}
jobs:
${jobLines.join("\n")}
${criticalLine}[/JOB_PREFERENCES]`;

    client.send({ text: recapMsg });
    console.log("DEBUG: Sending user action to model:", recapMsg);
    setIsShowingJobs(false);
    client.send({
      text: "Job suggestions reviewed. Continue with the current stage. Do NOT make a note about this message.",
    });
  }, [client, setIsShowingJobs, suggestedJobs]);

  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [hasStartedViewing, setHasStartedViewing] = useState(false);

  // Reset when new jobs arrive
  useEffect(() => {
    if (suggestedJobs.length > 0) {
      setCurrentJobIndex(0);
      setHasStartedViewing(false);
      jobActionsRef.current = {};
    }
  }, [suggestedJobs]);

  // Effect to handle sending restriction analysis when no jobs are found
  useEffect(() => {
    if (
      isShowingJobs &&
      suggestedJobs.length === 0 &&
      Object.keys(restrictionAnalysis).length > 0
    ) {
      let message = "";
      if (restrictionAnalysis.hasOwnProperty("GEMINI_UNAVAILABLE")) {
        message =
          "The AI service is currently unavailable. Please try again in a few moments.";
      } else if (restrictionAnalysis.hasOwnProperty("UNEXPECTED_ERROR")) {
        message = "An unexpected error occurred. Please try again.";
      } else {
        // Format the analysis into a string with percentages
        const reasons = Object.entries(restrictionAnalysis)
          .slice(0, 5) // Take top 5 reasons
          .map(
            ([reason, stats]) =>
              `${reason} (${stats.percentage.toFixed(1)}% of jobs)`,
          )
          .join(", ");

        message = `I was unable to find any suitable job matches for you. It seems the main limiting factors were: ${reasons}.`;
      }

      client.send({ text: message });
      console.log("DEBUG: Sending restriction analysis to model:", message);
    }
  }, [isShowingJobs, suggestedJobs, restrictionAnalysis, client]);

  // Send first job when jobs are shown
  useEffect(() => {
    if (isShowingJobs && suggestedJobs.length > 0 && !hasStartedViewing) {
      const firstJob = suggestedJobs[0];

      const jobDetails = Object.entries(firstJob)
        .filter(([, value]) => value != null && value !== "")
        .map(([key, value]) => `${key}: ${formatValue(value)}`)
        .join("\n");

      const jobDetailsMessage = `[JOB_CONTEXT]
job_index: 1 of ${suggestedJobs.length}
${jobDetails}
[/JOB_CONTEXT]
This is the job currently displayed to the user. Briefly introduce it in 1-2 sentences. Do NOT invent details. Wait for the user to click "Interested" or "Not Interested" before discussing another job.`;

      client.send({ text: jobDetailsMessage });
      console.log(
        "DEBUG: Sending first job details to model:",
        jobDetailsMessage,
      );
      setHasStartedViewing(true);
    }
  }, [isShowingJobs, suggestedJobs, hasStartedViewing, client]);

  const handleJobViewed = useCallback(
    (job: Job) => {
      const nextIndex = currentJobIndex + 1;

      if (nextIndex < suggestedJobs.length) {
        setCurrentJobIndex(nextIndex);
        const nextJob = suggestedJobs[nextIndex];

        const jobDetails = Object.entries(nextJob)
          .filter(([, value]) => value != null && value !== "")
          .map(([key, value]) => `${key}: ${formatValue(value)}`)
          .join("\n");

        const msg = `[JOB_CONTEXT]
job_index: ${nextIndex + 1} of ${suggestedJobs.length}
${jobDetails}
[/JOB_CONTEXT]
This is the next job now displayed to the user. Briefly introduce it in 1-2 sentences. Do NOT invent details. Wait for the user to click "Interested" or "Not Interested" before discussing another job.`;

        client.send({ text: msg });
        console.log("DEBUG: Sending next job details to model:", msg);

        return nextJob;
      } else {
        handleJobCompletion();
        return null;
      }
    },
    [client, currentJobIndex, suggestedJobs, handleJobCompletion],
  );

  const renderMainContent = () => {
    if (isLoadingJobs) {
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <CircularProgress size={48} />
          <Typography>{t("findingMatchingJobs", language)}</Typography>
        </div>
      );
    }
    return (
      <PointCloud
        isTransforming={false}
        onTogglePause={() => {}}
        volume={volume}
        connected={connected}
      />
    );
  };

  const mainContainerStyles: React.CSSProperties = {
    width: "100%",
    height: "100dvh",
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "white",
  };

  return (
    <div style={mainContainerStyles}>
      <Snackbar
        open={!isOnline}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          sx={{ width: "100%", fontSize: "1.1rem", padding: "16px 24px" }}
        >
          {t("offlineMessage", language)}
        </Alert>
      </Snackbar>
      <TermsAndConditionsDialog
        open={!hasAcceptedTerms}
        onAccept={handleAcceptTerms}
        countryCode={countryCode}
      />
      {!isShowingJobs && (
        <>
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Typography
              sx={{
                color: "black",
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                fontWeight: 500,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: { xs: "4px 8px", sm: "5px 10px", md: "6px 12px" },
                borderRadius: "20px",
                whiteSpace: "nowrap",
              }}
            >
              {t("reportYourIssue", language)}
            </Typography>
            <IconButton
              onClick={() => {
                setFeedbackMode("report");
                setShowFeedback(true);
              }}
              sx={{
                width: { xs: "40px", sm: "48px" },
                height: { xs: "40px", sm: "48px" },
                backgroundColor: "rgba(33,117,217,0.9)",
                color: "white",
                "&:hover": { backgroundColor: "rgb(45,129,229)" },
              }}
            >
              <FeedbackIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
            </IconButton>
          </div>
          <IconButton
            onClick={() => setShowSettings(true)}
            sx={{
              position: "absolute",
              top: { xs: "62px", sm: "78px" },
              right: { xs: "12px", sm: "20px" },
              zIndex: 20,
              width: { xs: "40px", sm: "48px" },
              height: { xs: "40px", sm: "48px" },
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              color: "rgb(33, 117, 217)",
              "&:hover": {
                backgroundColor: "rgb(33, 117, 217)",
                color: "white",
              },
              display: { xs: "flex", md: "none" },
            }}
          >
            <SettingsIcon sx={{ fontSize: { xs: "30px", sm: "35px" } }} />
          </IconButton>
        </>
      )}
      {showFeedback && (
        <FeedbackForm
          sessionId={sessionId!}
          onClose={() => setShowFeedback(false)}
          mode={feedbackMode}
          language={language}
        />
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          top: "10px",
          left: 0,
          right: 0,
          bottom: "80px",
          zIndex: 1,
          overflow: "hidden",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {renderMainContent()}
      </div>

      {isShowingJobs && (
        <>
          {/* Backdrop for job suggestions */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "80px 16px 160px 16px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "600px",
                height: "100%",
                display: "flex",
              }}
            >
              {suggestedJobs.length > 0 ? (
                <JobSuggestion
                  job={suggestedJobs[currentJobIndex]}
                  currentJobIndex={currentJobIndex}
                  totalJobs={suggestedJobs.length}
                  onJobPreference={handleJobPreference}
                  onComplete={handleJobCompletion}
                  onJobViewed={handleJobViewed}
                  countryCode={countryCode}
                />
              ) : (
                <Paper
                  elevation={8}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: "16px",
                    height: "fit-content",
                    marginTop: "28%",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t("noSuitableJobsFound", language)}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t("noMatchingJobsMessage", language)}
                  </Typography>
                  <button
                    className="action-button secondary"
                    onClick={handleJobCompletion}
                    style={{ display: "inline" }}
                  >
                    {t("close", language)}
                  </button>
                </Paper>
              )}
            </div>
          </div>
        </>
      )}

      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2 }}>
        <Header />
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 11,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "5px",
          paddingTop: "10px",
          paddingBottom: "max(env(safe-area-inset-bottom, 16px), 16px)",
          paddingLeft: "env(safe-area-inset-left, 8px)",
          paddingRight: "env(safe-area-inset-right, 8px)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 8px",
            paddingBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <IconButton
            ref={micButtonRef}
            onClick={handleMuteToggle}
            disabled={!connected}
            sx={micButtonStyle}
          >
            {isMuted ? <MicOff /> : <Mic />}
          </IconButton>
          <button
            ref={connectButtonRef}
            className="action-button secondary"
            onClick={handleConnectionToggle}
            disabled={isConnecting || !hasAcceptedTerms}
            style={{
              minWidth: "140px",
              flexShrink: 0,
            }}
          >
            {isConnecting
              ? t("starting", language)
              : connected
                ? t("end", language)
                : t("start", language)}
          </button>

          {!connected && (
            <>
              <FormControl
                sx={{
                  minWidth: "140px",
                  display: { xs: "none", md: "flex" },
                }}
              >
                <InputLabel id="language-select-label">
                  {t("language", language)}
                </InputLabel>
                <Select
                  labelId="language-select-label"
                  value={language}
                  label={t("language", language)}
                  onChange={(e) => setLanguage(e.target.value)}
                  sx={{
                    backgroundColor: "white",
                    height: "60px",
                  }}
                >
                  {availableLanguages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </div>
        <div style={{ width: "100%" }}>
          <ProfilePreview
            latestEntry={latestProfileEntry}
            allEntries={memoryKv}
            isExpanded={isProfileExpanded}
            onToggleExpand={() => setIsProfileExpanded(!isProfileExpanded)}
            language={language}
          />
        </div>
      </div>

      <EndConversationDialog
        open={showEndDialog}
        onConfirm={handleEndConfirm}
        onCancel={handleEndCancel}
        language={language}
      />

      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(10px)",
            borderRadius: "16px",
            padding: { xs: "16px", sm: "24px" },
            border: "1px solid rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            paddingBottom: "16px",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setShowSettings(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("settings", language)}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{ paddingTop: "8px", paddingX: { xs: "8px", sm: "24px" } }}
        >
          <FormControl fullWidth sx={{ marginBottom: 2, marginTop: 1 }}>
            <InputLabel id="settings-language-label">
              {t("language", language)}
            </InputLabel>
            <Select
              labelId="settings-language-label"
              value={language}
              label={t("language", language)}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {availableLanguages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", paddingTop: "16px" }}>
          <button
            className="action-button"
            onClick={() => setShowSettings(false)}
            style={{ minWidth: "120px" }}
          >
            {t("done", language)}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MainContent;
